import { useState } from 'react';

function JsonPrimitive({ value }: { value: unknown }) {
  if (value === null) return <span className="text-gray-500">null</span>;
  if (typeof value === 'boolean') return <span className="text-blue-400">{String(value)}</span>;
  if (typeof value === 'number') return <span className="text-yellow-400">{String(value)}</span>;
  if (typeof value === 'string') return <span className="text-emerald-400">&quot;{value}&quot;</span>;
  return <span className="text-gray-300">{String(value)}</span>;
}

function JsonNode({ keyName, value, depth }: { keyName?: string | number; value: unknown; depth: number }) {
  const [open, setOpen] = useState(depth <= 1);

  const keyLabel = keyName !== undefined
    ? typeof keyName === 'number'
      ? <span className="text-gray-400">{keyName}: </span>
      : <span><span className="text-sky-300">&quot;{keyName}&quot;</span><span className="text-gray-400">: </span></span>
    : null;

  const isPrimitive = value === null || typeof value !== 'object';

  if (isPrimitive) {
    return (
      <div className="leading-5">
        {keyLabel}
        <JsonPrimitive value={value} />
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries: [string | number, unknown][] = isArray
    ? (value as unknown[]).map((v, i) => [i, v])
    : Object.entries(value as Record<string, unknown>);
  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';
  const countLabel = isArray
    ? `${entries.length} item${entries.length !== 1 ? 's' : ''}`
    : `${entries.length} key${entries.length !== 1 ? 's' : ''}`;

  if (entries.length === 0) {
    return (
      <div className="leading-5">
        {keyLabel}
        <span className="text-gray-500">{openBracket}{closeBracket}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="leading-5 flex items-baseline">
        <button
          type="button"
          className="w-4 shrink-0 text-gray-500 hover:text-gray-200"
          onClick={() => setOpen(v => !v)}
        >
          {open ? '▼' : '▶'}
        </button>
        <span>
          {keyLabel}
          <span className="text-gray-400">{openBracket}</span>
          {!open && (
            <>
              <span className="text-gray-500 italic ml-1">{countLabel}</span>
              <span className="text-gray-400">{closeBracket}</span>
            </>
          )}
        </span>
      </div>
      {open && (
        <div className="ml-4">
          {entries.map(([k, v]) => (
            <JsonNode key={String(k)} keyName={k} value={v} depth={depth + 1} />
          ))}
          <div className="leading-5 text-gray-400">{closeBracket}</div>
        </div>
      )}
    </div>
  );
}

export function JsonTree({ data }: { data: unknown }) {
  return (
    <div className="px-4 py-3 bg-gray-950 text-xs font-mono overflow-x-auto">
      <JsonNode value={data} depth={0} />
    </div>
  );
}
